import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Loader2, CheckCircle } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { AuthentipetLogo } from '../../../components/AuthentipetLogo';
import { AuthLayout } from './AuthLayout';
import { UserGuideModal } from '../../onboarding/components/UserGuideModal';
import { Input } from '../../../components/ui/Input';
import { Button } from '../../../components/ui/Button';
import { validateEmail } from '../../../services/authService';

interface LoginProps {
  onMagicLink: (email: string) => Promise<{ success: boolean; message: string }>;
}

export const Login: React.FC<LoginProps> = ({ onMagicLink }) => {
  const { theme } = useTheme();
  const [email, setEmail] = useState('');
  const [isUserGuideOpen, setIsUserGuideOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ email?: string }>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setFieldErrors({});

    // Validate email
    const validation = validateEmail(email);
    if (!validation.valid) {
      setFieldErrors({ email: validation.errors.join(', ') });
      return;
    }

    setIsLoading(true);

    try {
      const result = await onMagicLink(email.trim().toLowerCase());
      if (result.success) {
        setSuccess(result.message);
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to send login link. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="sm:mx-auto sm:w-full sm:max-w-sm">
        <div className="flex justify-center">
          <div className="bg-white/10 p-2 rounded-full">
            <AuthentipetLogo size={80} />
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold font-league-spartan uppercase tracking-[-2px] text-storgia-teal">
          Authentipet
        </h2>
        <p className={`mt-2 text-center text-sm ${theme === 'dark' ? 'text-storgia-light-blurple/80' : 'text-gray-600'}`}>
          Log in to manage your portfolio
        </p>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
        <form className="space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="p-3 rounded bg-red-500/10 border border-red-500/20 text-red-500 text-sm text-center">
              {error}
            </div>
          )}

          {success && (
            <div className="p-4 rounded bg-green-500/10 border border-green-500/20 text-green-400 text-sm text-center flex items-center justify-center gap-2">
              <CheckCircle className="h-5 w-5" />
              <span>{success}</span>
            </div>
          )}

          {!success && (
            <>
              <Input
                label="Email address"
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (fieldErrors.email) setFieldErrors({});
                }}
                placeholder="owner@management.com"
                leftIcon={<Mail className={`h-5 w-5 ${theme === 'dark' ? 'text-storgia-light-blurple/50' : 'text-gray-400'}`} />}
                error={fieldErrors.email}
              />

              <div>
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-storgia-cta-pink hover:bg-storgia-cta-pink/90 text-white shadow-[0_0_20px_rgba(192,0,84,0.3)] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending...
                    </span>
                  ) : (
                    'Send login link'
                  )}
                </Button>
              </div>

              <p className={`text-center text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                We&apos;ll email you a magic link for instant, password-free sign in.
              </p>
            </>
          )}

          {success && (
            <div className="text-center">
              <p className={`text-sm mb-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                Didn&apos;t receive the email? Check your spam folder or
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSuccess(null);
                  setError(null);
                }}
                className="w-full"
              >
                Try again
              </Button>
            </div>
          )}
        </form>

        {/* User Guide Button */}
        <div className="mt-6 text-center space-y-4">
          <button
            onClick={() => setIsUserGuideOpen(true)}
            className={`text-sm font-medium underline hover:no-underline transition-all ${theme === 'dark' ? 'text-storgia-light-blurple hover:text-white' : 'text-storgia-blurple hover:text-storgia-dark-blurple'
              }`}
          >
            Need help? Read the User Guide
          </button>

          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
            Don&apos;t have an account?{' '}
            <Link to="/signup" className={`font-semibold leading-6 ${theme === 'dark' ? 'text-storgia-green hover:text-storgia-green/80' : 'text-storgia-cta-pink hover:text-storgia-cta-pink/80'}`}>
              Sign up
            </Link>
          </p>

          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-800">
            <a
              href="https://authentipets.com"
              className={`text-xs block ${theme === 'dark' ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600'}`}
            >
              If you are trying to get an ESA letter Verified Click here to navigate back to Authentipets.com.
            </a>
          </div>
        </div>
      </div>

      <UserGuideModal
        isOpen={isUserGuideOpen}
        onClose={() => setIsUserGuideOpen(false)}
      />
    </AuthLayout>
  );
};
